import { useCallback } from 'react'
import { captureMessage } from '@sentry/react'

import { BRAZE_API_KEY, BRAZE_BASE_URL } from 'app/utils/constants'

// Type definitions for Braze SDK
interface BrazeOptions {
    baseUrl: string
    enableLogging?: boolean
    allowUserSuppliedJavascript?: boolean
    [key: string]: any
}

interface BrazeSDK {
    initialize: (apiKey: string, options: BrazeOptions) => void
    changeUser: (userId: string) => void
    openSession: () => void
    logCustomEvent: (eventName: string, eventProperties: Record<string, any>) => void
    [key: string]: any
}

/**
 * Returns true when the error is caused by an ad blocker or privacy-focused browser
 * intercepting the Braze SDK chunk load or its subsequent network requests.
 * These failures are user-side and not actionable via code changes.
 */
function isBrazeBlockedError(error: unknown): boolean {
    if (!(error instanceof Error)) return false
    const name = error.name ?? ''
    const message = error.message ?? ''
    // Vite emits "ChunkLoadError" when a dynamic import chunk cannot be fetched.
    if (name === 'ChunkLoadError') return true
    // Browsers emit "TypeError: Failed to fetch" / "TypeError: Load failed" when a
    // network request is blocked (e.g. by an extension or privacy setting).
    if (name === 'TypeError' && /failed to fetch|load failed|network request failed/i.test(message)) return true
    return false
}

// Custom hook to handle Braze SDK operations
const useBraze = () => {
    const executeBrazeOperation = useCallback(async (operation: (braze: BrazeSDK) => void) => {
        if (typeof window !== 'undefined') {
            try {
                const braze = await import('@braze/web-sdk')
                operation(braze as BrazeSDK)
            } catch (error) {
                // Ad blockers and privacy-focused browsers commonly block the Braze SDK
                // chunk or its network calls. These failures are not actionable on our end.
                if (isBrazeBlockedError(error)) {
                    return
                }
                captureMessage('Failed to load Braze SDK:', {
                    level: 'warning',
                    extra: {
                        error
                    }
                })
            }
        }
    }, [])

    const initializeBraze = useCallback(async (options: Omit<BrazeOptions, 'baseUrl'>) => {
        if (!BRAZE_API_KEY || !BRAZE_BASE_URL) {
            captureMessage('Braze configuration missing, skipping initialization', {
                level: 'error',
                extra: {
                    BRAZE_API_KEY,
                    BRAZE_BASE_URL
                }
            })
            return
        }
        if (typeof window !== 'undefined') {
            try {
                const braze = await import('@braze/web-sdk')
                ;(braze as BrazeSDK).initialize(BRAZE_API_KEY, {
                    baseUrl: BRAZE_BASE_URL,
                    ...options
                })
            } catch (error) {
                // Ad blockers and privacy-focused browsers commonly block the Braze SDK
                // chunk or its network calls. These failures are not actionable on our end.
                if (isBrazeBlockedError(error)) {
                    return
                }
                captureMessage('Failed to initialize Braze:', {
                    level: 'warning',
                    extra: {
                        error
                    }
                })
            }
        }
    }, [])

    return {
        executeBrazeOperation,
        initializeBraze,
        BRAZE_EVENTS: {
            VIEWED_OFFER_STEP: 'Viewed Offer Page',
            VIEWED_OFFER_STEP_UNVERIFIED: 'Viewed Offer Page (Unverified)',
            VIEWED_CUSTOMIZE_OFFER_STEP: 'Viewed Customize Offer Page',
            VIEWED_START_ONFIDO_STEP: 'Viewed Start Onfido Page',
            VIEWED_START_FLINKS_STEP: 'Viewed Start Flinks Page',
            VIEWED_CHANGE_PAYMENT_METHOD_STEP: 'Viewed Change Payment Method Page',
            VIEWED_CONFIRM_PAYMENT_SCHEDULE_STEP: 'Viewed Confirm Payment Schedule Page',
            VIEWED_PAYMENT_PROTECTION_OPT_IN_STEP: 'Viewed Payment Protection Opt-In Page',
            VIEWED_REVIEW_STEP: 'Viewed Review Page',
            VIEWED_LOAN_BOOKING_SUCCESS_STEP: 'Viewed Loan Booking Success Page'
        }
    }
}

export default useBraze
